import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Form, OverlayTrigger, Table, Tooltip } from 'react-bootstrap';
import { FaSync } from 'react-icons/fa';
import 'react-datepicker/dist/react-datepicker.css';
import '../Pages/Activities.css';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import { API_BASE_URL } from '../config';
import { useTenant } from '../context/TenantContext';
import PermissionGuard from './PermissionGuard';
import { PERMISSIONS } from '../constants/permissions';
import {
    STAFF_TYPE_ABBR_MAX,
    STAFF_TYPE_NAME_MAX,
    normalizeStaffLabel,
    useStaffTypes,
} from '../hooks/useStaffTypes';

const StaffPrices = () => {
    const { currentTenant } = useTenant();
    const {
        staffTypes,
        getDisplayName,
        getAbbreviation,
        updateStaffType,
        refresh: refreshStaffTypes,
        tenantQs,
        isLoading: typesLoading,
    } = useStaffTypes();

    const [staffPrices, setStaffPrices] = useState([]);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
    const [years, setYears] = useState([]);
    const [editedPrices, setEditedPrices] = useState({});
    const [labelDrafts, setLabelDrafts] = useState({});
    const [savingLabelId, setSavingLabelId] = useState(null);
    const [savingPriceId, setSavingPriceId] = useState(null);
    const [toast, setToast] = useState(null);
    const [newYear, setNewYear] = useState('');
    const [newYearRates, setNewYearRates] = useState({ SN: '', ENA: '', CW: '' });
    const [showAddYear, setShowAddYear] = useState(false);

    const showMessage = (message, variant = 'success') => {
        setToast({ message, variant });
        setTimeout(() => setToast(null), 3000);
    };

    const fetchStaffPrices = useCallback(async () => {
        if (!currentTenant?.id) {
            setStaffPrices([]);
            setYears([]);
            return;
        }
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/staff/prices${tenantQs}`);
            if (!response.ok) {
                console.error('Failed to fetch staff prices');
                return;
            }
            const data = await response.json();
            const transformedData = (Array.isArray(data) ? data : []).map((item) => ({
                StaffID: item.id || item.flow_staff_id,
                StaffType: item.staff_type_code || item.flow_staff_type,
                Year: String(item.year),
                StandardHourlyPrice: item.standard_hourly_price,
            }));
            setStaffPrices(transformedData);
            const uniqueYears = Array.from(new Set(transformedData.map((item) => item.Year))).sort(
                (a, b) => String(b).localeCompare(String(a))
            );
            setYears(uniqueYears);
            if (uniqueYears.length > 0) {
                setSelectedYear((prev) => (uniqueYears.includes(prev) ? prev : uniqueYears[0]));
            }
        } catch (error) {
            console.error('An error occurred while fetching staff prices:', error);
        }
    }, [currentTenant?.id, tenantQs]);

    useEffect(() => {
        fetchStaffPrices();
    }, [fetchStaffPrices]);

    const handleRefresh = async () => {
        setLabelDrafts({});
        await Promise.all([refreshStaffTypes(), fetchStaffPrices()]);
    };

    useEffect(() => {
        setLabelDrafts({});
    }, [currentTenant?.id]);

    const handlePriceInputChange = (id, value) => {
        setEditedPrices((prev) => ({ ...prev, [id]: value }));
    };

    const isPriceDirty = (staff) => {
        if (editedPrices[staff.StaffID] === undefined) return false;
        const edited = String(editedPrices[staff.StaffID]).trim();
        const saved = String(staff.StandardHourlyPrice ?? '').trim();
        const editedNum = parseFloat(edited);
        const savedNum = parseFloat(saved);
        if (edited !== '' && !Number.isNaN(editedNum) && !Number.isNaN(savedNum)) {
            return editedNum !== savedNum;
        }
        return edited !== saved;
    };

    const handleSavePrice = async (id) => {
        const newPrice = editedPrices[id];
        if (newPrice === undefined || newPrice === '') return;
        setSavingPriceId(id);
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/staff/prices/${id}${tenantQs}`, {
                method: 'PUT',
                body: JSON.stringify({
                    standard_hourly_price: parseFloat(newPrice),
                    tenant_id: currentTenant.id,
                }),
            });
            if (!response.ok) {
                throw new Error('Failed to update staff price');
            }
            const updatedPrice = await response.json();
            setStaffPrices((prev) =>
                prev.map((price) =>
                    price.StaffID === id
                        ? {
                              ...price,
                              StandardHourlyPrice:
                                  updatedPrice.standard_hourly_price ??
                                  updatedPrice.StandardHourlyPrice,
                          }
                        : price
                )
            );
            setEditedPrices((prev) => {
                const next = { ...prev };
                delete next[id];
                return next;
            });
            showMessage('Staff price saved');
        } catch (error) {
            console.error('An error occurred while updating staff price:', error);
            showMessage('Failed to save staff price', 'danger');
        } finally {
            setSavingPriceId(null);
        }
    };

    const labelFor = (type) => ({
        abbreviation: labelDrafts[type.code]?.abbreviation ?? type.abbreviation ?? type.code,
        display_name: labelDrafts[type.code]?.display_name ?? type.display_name ?? type.code,
    });

    const handleLabelInputChange = (code, field, value) => {
        setLabelDrafts((prev) => {
            const current = staffTypes.find((type) => type.code === code);
            return {
                ...prev,
                [code]: {
                    abbreviation: current?.abbreviation ?? code,
                    display_name: current?.display_name ?? code,
                    ...(prev[code] || {}),
                    [field]: value,
                },
            };
        });
    };

    const isLabelDirty = (type) => {
        const draft = labelFor(type);
        return (
            normalizeStaffLabel(draft.abbreviation) !== normalizeStaffLabel(type.abbreviation) ||
            normalizeStaffLabel(draft.display_name) !== normalizeStaffLabel(type.display_name)
        );
    };

    const handleSaveLabel = async (type) => {
        if (!type?.id) {
            showMessage('Staff types failed to load. Refresh and try again.', 'danger');
            return;
        }
        const draft = labelFor(type);
        const abbreviation = normalizeStaffLabel(draft.abbreviation);
        const display_name = normalizeStaffLabel(draft.display_name);
        if (!abbreviation || !display_name) {
            showMessage('Abbreviation and display name are required', 'danger');
            return;
        }
        if (abbreviation.length > STAFF_TYPE_ABBR_MAX) {
            showMessage(`Abbreviation must be ${STAFF_TYPE_ABBR_MAX} characters or fewer`, 'danger');
            return;
        }
        if (display_name.length > STAFF_TYPE_NAME_MAX) {
            showMessage(`Display name must be ${STAFF_TYPE_NAME_MAX} characters or fewer`, 'danger');
            return;
        }
        const taken = staffTypes.some((other) => {
            if (other.code === type.code) return false;
            return normalizeStaffLabel(labelFor(other).abbreviation).toLowerCase() === abbreviation.toLowerCase();
        });
        if (taken) {
            showMessage('Each staff type needs a unique abbreviation', 'danger');
            return;
        }
        setSavingLabelId(type.id);
        try {
            await updateStaffType(type.id, { abbreviation, display_name });
            setLabelDrafts((prev) => {
                const next = { ...prev };
                delete next[type.code];
                return next;
            });
            showMessage('Staff type label saved');
        } catch (error) {
            console.error(error);
            showMessage(error.message || 'Failed to save label', 'danger');
        } finally {
            setSavingLabelId(null);
        }
    };

    const handleAddNewYear = async () => {
        if (!newYear.trim()) {
            showMessage('Enter a year', 'danger');
            return;
        }
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/staff/prices${tenantQs}`, {
                method: 'POST',
                body: JSON.stringify({
                    tenant_id: currentTenant.id,
                    year: newYear.trim(),
                    sn: parseFloat(newYearRates.SN) || 0,
                    ena: parseFloat(newYearRates.ENA) || 0,
                    cw: parseFloat(newYearRates.CW) || 0,
                }),
            });
            if (!response.ok) {
                throw new Error('Failed to add new year');
            }
            await fetchStaffPrices();
            setSelectedYear(newYear.trim());
            setNewYear('');
            setNewYearRates({ SN: '', ENA: '', CW: '' });
            setShowAddYear(false);
            showMessage('Year rates added');
        } catch (error) {
            console.error(error);
            showMessage('Failed to add new year', 'danger');
        }
    };

    const typeSortIndex = useMemo(() => {
        const map = {};
        staffTypes.forEach((type, index) => {
            map[type.code] = type.sort_order ?? index;
        });
        return map;
    }, [staffTypes]);

    const filteredStaffPrices = useMemo(
        () =>
            staffPrices
                .filter((staff) => staff.Year === selectedYear)
                .slice()
                .sort((a, b) => {
                    const aOrder = typeSortIndex[a.StaffType] ?? 999;
                    const bOrder = typeSortIndex[b.StaffType] ?? 999;
                    if (aOrder !== bOrder) return aOrder - bOrder;
                    return String(a.StaffType).localeCompare(String(b.StaffType));
                }),
        [staffPrices, selectedYear, typeSortIndex]
    );

    if (!currentTenant?.id) {
        return (
            <div className="activities-container">
                <div className="page-hero">
                    <div className="page-hero-text">
                        <h1 className="page-hero-title">Staff Prices</h1>
                        <p className="page-hero-subtitle">Select a tenant to manage staff types and rates</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="activities-container">
            <div className="page-hero">
                <div className="page-hero-text">
                    <h1 className="page-hero-title">Staff Prices</h1>
                    <p className="page-hero-subtitle">
                        Configure staff type labels and standard hourly rates for {currentTenant.name}
                    </p>
                </div>
                <div className="page-hero-actions">
                    <div className="page-stat-chip">
                        <div className="page-stat-value">{staffTypes.length}</div>
                        <div className="page-stat-label">Staff Types</div>
                    </div>
                </div>
            </div>

            {toast && (
                <div
                    className={`alert alert-${toast.variant === 'danger' ? 'danger' : 'success'} py-2`}
                    role="alert"
                >
                    {toast.message}
                </div>
            )}

            <div className="page-toolbar" style={{ marginBottom: 8 }}>
                <div className="page-toolbar-left">
                    <h2 style={{ fontSize: '1.05rem', fontWeight: 650, margin: 0 }}>Staff type labels</h2>
                    <p style={{ margin: '6px 0 0', fontSize: '0.82rem', color: 'var(--theme-text-secondary)' }}>
                        Internal codes stay SN, ENA and CW. The abbreviation is what tables show; the display name
                        appears on hover and in charts.
                    </p>
                </div>
                <div className="page-toolbar-actions">
                    <Button
                        variant="outline-primary"
                        onClick={handleRefresh}
                        disabled={typesLoading}
                    >
                        <FaSync className={typesLoading ? 'fa-spin' : ''} style={{ marginRight: 6 }} />
                        Refresh
                    </Button>
                </div>
            </div>

            <div
                className="table-wrapper"
                style={{
                    overflowX: 'auto',
                    borderRadius: 12,
                    border: '1px solid var(--theme-table-border)',
                    marginBottom: 28,
                }}
            >
                <Table striped bordered hover style={{ margin: 0 }}>
                    <thead>
                        <tr>
                            <th style={{ width: 110, verticalAlign: 'middle', textAlign: 'center' }}>Code</th>
                            <th style={{ verticalAlign: 'middle' }}>Abbreviation</th>
                            <th style={{ verticalAlign: 'middle' }}>Display name</th>
                            <th style={{ width: 110, verticalAlign: 'middle' }}>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {staffTypes.map((type) => (
                            <tr key={type.code}>
                                <td style={{ verticalAlign: 'middle', textAlign: 'center' }}>
                                    <span
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            minWidth: 52,
                                            padding: '4px 10px',
                                            borderRadius: 6,
                                            background: 'var(--theme-accent-soft)',
                                            color: 'var(--theme-accent)',
                                            fontSize: '0.8rem',
                                            fontWeight: 700,
                                            letterSpacing: '0.04em',
                                            lineHeight: 1.2,
                                        }}
                                    >
                                        {type.code}
                                    </span>
                                </td>
                                <td style={{ verticalAlign: 'middle' }}>
                                    <Form.Control
                                        type="text"
                                        value={labelFor(type).abbreviation}
                                        maxLength={STAFF_TYPE_ABBR_MAX}
                                        autoComplete="off"
                                        aria-label={`${type.code} abbreviation`}
                                        onChange={(e) =>
                                            handleLabelInputChange(type.code, 'abbreviation', e.target.value)
                                        }
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && type.id && isLabelDirty(type)) {
                                                e.preventDefault();
                                                handleSaveLabel(type);
                                            }
                                        }}
                                        style={{ height: 38 }}
                                    />
                                </td>
                                <td style={{ verticalAlign: 'middle' }}>
                                    <Form.Control
                                        type="text"
                                        value={labelFor(type).display_name}
                                        maxLength={STAFF_TYPE_NAME_MAX}
                                        autoComplete="off"
                                        aria-label={`${type.code} display name`}
                                        onChange={(e) =>
                                            handleLabelInputChange(type.code, 'display_name', e.target.value)
                                        }
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && type.id && isLabelDirty(type)) {
                                                e.preventDefault();
                                                handleSaveLabel(type);
                                            }
                                        }}
                                        style={{ height: 38 }}
                                    />
                                </td>
                                <td style={{ verticalAlign: 'middle' }}>
                                    {(() => {
                                        const dirty = isLabelDirty(type);
                                        const saving = savingLabelId === type.id;
                                        const canSave = Boolean(type.id) && dirty && !saving;
                                        const saveButton = (
                                            <Button
                                                variant="primary"
                                                disabled={!canSave}
                                                onClick={() => handleSaveLabel(type)}
                                                style={{ height: 38, minWidth: 84 }}
                                            >
                                                {saving ? 'Saving…' : 'Save'}
                                            </Button>
                                        );

                                        const guarded = (
                                            <PermissionGuard
                                                permission={PERMISSIONS.STAFF_PRICING_UPDATE}
                                                showDisabled
                                                tooltip="You do not have permission to update staff type labels"
                                            >
                                                {saveButton}
                                            </PermissionGuard>
                                        );

                                        if (!type.id) {
                                            return (
                                                <OverlayTrigger
                                                    placement="top"
                                                    overlay={
                                                        <Tooltip id={`missing-id-label-${type.code}`}>
                                                            Staff types failed to load. Refresh and try again.
                                                        </Tooltip>
                                                    }
                                                >
                                                    <span className="d-inline-block">{guarded}</span>
                                                </OverlayTrigger>
                                            );
                                        }

                                        if (type.id && !dirty && !saving) {
                                            return (
                                                <OverlayTrigger
                                                    placement="top"
                                                    overlay={
                                                        <Tooltip id={`no-changes-label-${type.code}`}>
                                                            No changes made
                                                        </Tooltip>
                                                    }
                                                >
                                                    <span className="d-inline-block">{guarded}</span>
                                                </OverlayTrigger>
                                            );
                                        }

                                        return guarded;
                                    })()}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>

            <div className="page-toolbar">
                <div className="page-toolbar-left" style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <h2 style={{ fontSize: '1.05rem', fontWeight: 650, margin: 0 }}>Hourly rates</h2>
                    <Form.Group controlId="yearSelect" className="mb-0">
                        <Form.Control
                            as="select"
                            value={selectedYear}
                            onChange={(e) => setSelectedYear(e.target.value)}
                            style={{ height: 40, borderRadius: 8, minWidth: 160 }}
                        >
                            {years.length === 0 && <option value={selectedYear}>{selectedYear}</option>}
                            {years.map((year) => (
                                <option key={year} value={year}>
                                    {year}
                                </option>
                            ))}
                        </Form.Control>
                    </Form.Group>
                </div>
                <PermissionGuard
                    permission={PERMISSIONS.STAFF_PRICING_UPDATE}
                    showDisabled
                    tooltip="You do not have permission to add year rates"
                >
                    <Button variant="outline-primary" onClick={() => setShowAddYear((v) => !v)}>
                        {showAddYear ? 'Cancel' : 'Add year'}
                    </Button>
                </PermissionGuard>
            </div>

            {showAddYear && (
                <div
                    style={{
                        border: '1px solid var(--theme-border)',
                        borderRadius: 12,
                        padding: 16,
                        marginBottom: 16,
                        background: 'var(--theme-surface-muted)',
                    }}
                >
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'end' }}>
                        <Form.Group controlId="newYear" className="mb-0">
                            <Form.Label>Year</Form.Label>
                            <Form.Control
                                type="text"
                                value={newYear}
                                onChange={(e) => setNewYear(e.target.value)}
                                placeholder="e.g. 2026"
                                style={{ minWidth: 120 }}
                            />
                        </Form.Group>
                        {staffTypes.map((type) => (
                            <Form.Group key={type.code} controlId={`newYear-${type.code}`} className="mb-0">
                                <Form.Label>{getAbbreviation(type.code)} price</Form.Label>
                                <Form.Control
                                    type="number"
                                    value={newYearRates[type.code]}
                                    onChange={(e) =>
                                        setNewYearRates((prev) => ({ ...prev, [type.code]: e.target.value }))
                                    }
                                    placeholder="0"
                                    style={{ minWidth: 120 }}
                                />
                            </Form.Group>
                        ))}
                        <Button variant="primary" onClick={handleAddNewYear}>
                            Create year
                        </Button>
                    </div>
                </div>
            )}

            <div
                className="table-wrapper"
                style={{ overflowX: 'auto', borderRadius: 12, border: '1px solid var(--theme-table-border)' }}
            >
                <Table striped bordered hover style={{ margin: 0, tableLayout: 'fixed', width: '100%' }}>
                    <thead>
                        <tr>
                            <th style={{ width: '32%', verticalAlign: 'middle', textAlign: 'left', padding: '12px 20px' }}>
                                Staff type
                            </th>
                            <th style={{ width: '18%', verticalAlign: 'middle', textAlign: 'center', padding: '12px 20px' }}>
                                Abbreviation
                            </th>
                            <th style={{ width: '32%', verticalAlign: 'middle', padding: '12px 20px' }}>
                                Standard hourly price
                            </th>
                            <th style={{ width: '18%', verticalAlign: 'middle', padding: '12px 20px' }}>
                                Action
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredStaffPrices.length === 0 ? (
                            <tr>
                                <td colSpan={4} className="text-muted text-center py-3">
                                    No rates for {selectedYear}. Add a year to get started.
                                </td>
                            </tr>
                        ) : (
                            filteredStaffPrices.map((staff) => (
                                <tr key={staff.StaffID}>
                                    <td style={{ verticalAlign: 'middle', textAlign: 'left', padding: '14px 20px' }}>
                                        {getDisplayName(staff.StaffType)}
                                    </td>
                                    <td style={{ verticalAlign: 'middle', textAlign: 'center', padding: '14px 20px' }}>
                                        <span
                                            title={
                                                getDisplayName(staff.StaffType) !== getAbbreviation(staff.StaffType)
                                                    ? `${getAbbreviation(staff.StaffType)} — ${getDisplayName(staff.StaffType)}`
                                                    : getAbbreviation(staff.StaffType)
                                            }
                                            style={{
                                                display: 'inline-block',
                                                maxWidth: 88,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                verticalAlign: 'middle',
                                                minWidth: 52,
                                                padding: '4px 10px',
                                                borderRadius: 6,
                                                background: 'var(--theme-accent-soft)',
                                                color: 'var(--theme-accent)',
                                                fontSize: '0.8rem',
                                                fontWeight: 700,
                                                letterSpacing: '0.04em',
                                                lineHeight: 1.2,
                                                textAlign: 'center',
                                            }}
                                        >
                                            {getAbbreviation(staff.StaffType)}
                                        </span>
                                    </td>
                                    <td style={{ verticalAlign: 'middle', padding: '14px 20px' }}>
                                        <Form.Control
                                            type="number"
                                            value={
                                                editedPrices[staff.StaffID] !== undefined
                                                    ? editedPrices[staff.StaffID]
                                                    : staff.StandardHourlyPrice
                                            }
                                            onChange={(e) => {
                                                const newValue = e.target.value;
                                                handlePriceInputChange(
                                                    staff.StaffID,
                                                    newValue === '' ? '' : newValue
                                                );
                                            }}
                                            style={{ height: 38, maxWidth: 200 }}
                                        />
                                    </td>
                                    <td style={{ verticalAlign: 'middle', padding: '14px 20px' }}>
                                        {(() => {
                                            const dirty = isPriceDirty(staff);
                                            const saving = savingPriceId === staff.StaffID;
                                            const canSave = dirty && !saving;
                                            const saveButton = (
                                                <Button
                                                    variant="primary"
                                                    disabled={!canSave}
                                                    onClick={() => handleSavePrice(staff.StaffID)}
                                                    style={{ height: 38, minWidth: 84 }}
                                                >
                                                    {saving ? 'Saving…' : 'Save'}
                                                </Button>
                                            );

                                            const guarded = (
                                                <PermissionGuard
                                                    permission={PERMISSIONS.STAFF_PRICING_UPDATE}
                                                    showDisabled
                                                    tooltip="You do not have permission to update staff prices"
                                                >
                                                    {saveButton}
                                                </PermissionGuard>
                                            );

                                            if (!dirty && !saving) {
                                                return (
                                                    <OverlayTrigger
                                                        placement="top"
                                                        overlay={
                                                            <Tooltip id={`no-changes-price-${staff.StaffID}`}>
                                                                No changes made
                                                            </Tooltip>
                                                        }
                                                    >
                                                        <span className="d-inline-block">{guarded}</span>
                                                    </OverlayTrigger>
                                                );
                                            }

                                            return guarded;
                                        })()}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </Table>
            </div>
        </div>
    );
};

export default StaffPrices;
